import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import ProgressIndicator from './studio/ProgressIndicator';
import UserMenu from './UserMenu';
import logo from '../assets/cc-studio-logo.png';
import { useTenant } from '../contexts/TenantContext';
import { usePlatformAdmin } from '../hooks/usePlatformAdmin';

interface TopBarProps {
  tenantName: string;
  userEmail: string;
  currentJob?: {
    jobId: string;
    status: 'queued' | 'claimed' | 'processing' | 'completed' | 'failed' | 'pending' | 'running';
    progress: number;
    stage: string;
    message: string;
  } | null;
}

const TopBar: React.FC<TopBarProps> = ({ tenantName, userEmail, currentJob }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser } = useTenant();
  const { isPlatformAdmin } = usePlatformAdmin();

  const userRole = currentUser?.role || 'member';
  const isOwner = userRole === 'owner';
  const isOnWorkflowsPage = location.pathname.startsWith('/workflows');
  const isOnAdminPage = location.pathname.startsWith('/admin');

  const navBtnBase = 'touch-target inline-flex items-center text-label font-medium transition-colors duration-base px-1 pb-0.5';
  const navActive  = 'text-blue-500 border-b-2 border-blue-500';
  const navIdle    = 'text-gray-500 hover:text-gray-900 border-b-2 border-transparent';

  return (
    <div className="sticky top-0 z-40 bg-surface-panel border-b border-gray-200 shadow-xs pt-[env(safe-area-inset-top)]">
      <div className="min-h-14 safe-x py-2 flex flex-wrap md:flex-nowrap items-center justify-between gap-x-4 gap-y-2">
        {/* Left — Logo & nav */}
        <div className="flex items-center gap-3 md:gap-5 min-w-0 flex-1 md:flex-none">
          <button type="button" className="touch-target cursor-pointer flex-shrink-0 flex items-center" onClick={() => navigate('/')} aria-label="Go to Studio home">
            <img src={logo} alt="CC Studio" className="h-8 w-auto max-w-[148px] sm:max-w-[176px] object-contain" />
          </button>
          <div className="hidden sm:block h-6 w-px bg-gray-200" />
          <span className="text-label font-medium text-gray-700 truncate max-w-[38vw] md:max-w-56" title={tenantName}>{tenantName}</span>

          {(isOwner || isPlatformAdmin) && (
            <>
              <div className="hidden sm:block h-6 w-px bg-gray-200" />
              <nav className="flex items-center gap-2 sm:gap-4 md:gap-5 overflow-x-auto scrollbar-touch -mx-1 px-1">
                <button type="button" onClick={() => navigate('/')}
                  className={`${navBtnBase} ${!isOnWorkflowsPage && !isOnAdminPage ? navActive : navIdle}`}>
                  Studio
                </button>
                {(isOwner || isPlatformAdmin) && (
                  <button type="button" onClick={() => navigate('/workflows')}
                    className={`${navBtnBase} ${isOnWorkflowsPage ? navActive : navIdle}`}>
                    Workflows
                  </button>
                )}
                {isPlatformAdmin && (
                  <button type="button" onClick={() => navigate('/admin')}
                    className={`${navBtnBase} ${isOnAdminPage ? navActive : navIdle}`}>
                    Admin
                  </button>
                )}
              </nav>
            </>
          )}
        </div>

        {/* Center — Progress */}
        {currentJob !== undefined && (
          <div className="order-last md:order-none w-full md:flex-1 md:max-w-sm md:mx-4 lg:mx-8">
            <ProgressIndicator currentJob={currentJob} />
          </div>
        )}

        {/* Right — User menu */}
        <div className="flex items-center flex-shrink-0">
          <UserMenu />
        </div>
      </div>
    </div>
  );
};

export default TopBar;

